// src/services/workoutGenerator.js
import dayjs from "dayjs";
import { supabase } from ".././supabaseClient";
import { buildSwotPriorityPayload } from "../lib/swotPriorityRouter";

export const generateDailyWorkout = async ({
  userId,
  selectedProgram,
  monthlyOutlines,
  selectedSwot = null,
  baseballFocusDomain = "all",
  workoutLength,
  onStatusUpdate = () => {}, // Default empty function if not provided
}) => {
  // 1. Validation
  if (!selectedProgram || !monthlyOutlines[0]) {
    throw new Error("Please select a program and monthly overview.");
  }

  console.log(userId)
  console.log(selectedProgram)
  console.log(workoutLength)

  const today = dayjs().format("YYYY-MM-DD");
  const monthlyOverview = monthlyOutlines[0];
  const swotPriorityPayload = buildSwotPriorityPayload(selectedSwot, {
    sport: selectedProgram?.sport,
    baseballFocusDomain
  });
  const swotPriorityFocus = Array.isArray(swotPriorityPayload?.season_priority_focus_items)
    ? swotPriorityPayload.season_priority_focus_items
    : [];
  const monthlyFocus = JSON.parse(monthlyOverview.focus_areas || "[]");
  const effectiveFocusAreas = swotPriorityFocus.length > 0 ? swotPriorityFocus : monthlyFocus;
  const priorityPromptDirectives = {
    priority_execution_order: "Use Priority #1 as primary until complete, then Priority #2, then Priority #3.",
    session_focus_rotation: "Do not skip ahead in the priority queue.",
    allocation_model: "Allocate approximately 50% of session to active priority queue, 25% to strengths reinforcement, 25% to weaknesses correction.",
    spiral_curriculum: "Maintain spiral curriculum by revisiting prior concepts while increasing difficulty and context each week.",
    opening_drill_rule: "The first drills of each workout must directly target the workout's main priority focus.",
    weekly_focus_pattern: "When one priority is completed, promote the next priority immediately.",
    completion_progression_rule: "When an item or priority is marked completed, stop using it as workout focus and automatically continue with the next active item/priority."
  };

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error("You must be logged in.");
    }

    // 2. Insert Base Workout Row
    const { data: workoutRow, error: workoutError } = await supabase
      .from("skill_workouts")
      .insert({
        athlete_id: userId, // Ensure this is a string ID
        program_id: selectedProgram,
        workout_date: today,
        duration_minutes: Number(workoutLength),
        focus: effectiveFocusAreas.join(", "),
        ai_generated: true,
        created_at: dayjs().toISOString(),
      })
      .select()
      .single();

    if (workoutError) throw workoutError;

    // 3. Fetch Context Data (Weekly Plan)
    const { data: weeklyRow } = await supabase
      .from("weekly_skill_workouts")
      .select("*")
      .eq("plan_id", monthlyOverview.id)
      .lte("start_date", today)
      .gte("end_date", today)
      .limit(1)
      .single();

    // 4. Fetch History (Last 7 Days)
    const sevenDaysAgo = dayjs().subtract(6, "day").startOf("day").toISOString();
    const todayISO = dayjs().endOf("day").toISOString();

    const { data: recentDrills } = await supabase
      .from("workout_drills")
      .select("drill_name, duration_minutes")
      .gte("created_at", sevenDaysAgo)
      .lte("created_at", todayISO);

    const previousScores = (recentDrills || []).reduce((acc, d) => {
      acc[d.drill_name] = d.duration_minutes;
      return acc;
    }, {});

    // 5. Call AI Edge Function
    if (onStatusUpdate) onStatusUpdate("Analyzing progress and generating drills...");

    const payload = {
      user_current_scores: JSON.parse(monthlyOverview.user_skill_scores || "{}"),
      program_scores: JSON.parse(monthlyOverview.program_skill_scores || "{}"),
      focus_areas: effectiveFocusAreas,
      previous_scores: previousScores,
      weekly_row_data: weeklyRow || {},
      start_date: today,
      current_age: userId.grade || 11,
      target_age: selectedProgram.player_peaking || 18,
      sport: selectedProgram.sport || "basketball",
      baseball_focus_domain: baseballFocusDomain,
      swot_priorities: swotPriorityPayload || null,
      swot_skill_categories: swotPriorityPayload?.skill_categories || null,
      season_priorities: Array.isArray(swotPriorityPayload?.skill_priorities) ? swotPriorityPayload.skill_priorities : [],
      completed_priority_items: [],
      season_priority_focus_items: swotPriorityFocus,
      priority_prompt_directives: priorityPromptDirectives,
      workout_length: Number(workoutLength),
    };

    const res = await fetch(
      "https://kzklgxnbtquhjfywtnsz.functions.supabase.co/generateSkillWorkouts",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) throw new Error("AI generation failed. Check Edge Function logs.");

    if (onStatusUpdate) onStatusUpdate("Finalizing drills and saving your plan...");
    const data = await res.json();

    // 6. Parse Response (support both direct JSON and legacy wrapped shapes)
    const workoutJSON = (() => {
      if (data && typeof data === "object" && data.blocks) return data;
      const rawContent = data?.choices?.[0]?.message?.content;
      if (!rawContent) {
        throw new Error("Workout response was missing generated content.");
      }
      const cleaned = String(rawContent).replace(/```json/g, "").replace(/```/g, "").trim();
      return JSON.parse(cleaned);
    })();

    // 7. Process Drills
    // Combine blocks safely
    const blocks = workoutJSON.blocks || {};
    const block1 = blocks["Block 1 — Review and Reinforce"] || [];
    const block2 = blocks["Block 2 — Progression Drills"] || [];
    const block3 = blocks["Block 3 — Game Situation Integration"] || [];

    const allDrills = [...block1, ...block2, ...block3].map((d) => ({
      workout_id: workoutRow.id,
      drill_name: d.drill_name.trim(),
      description: d.description,
      duration_minutes: d.duration || d.duration_minutes, // Handle both key names
      sets: d.sets,
      reps: d.reps,
      created_at: dayjs().toISOString(),
    }));

    // Remove duplicates
    const drillsToInsert = allDrills.filter(
      (drill, index, arr) =>
        index === arr.findIndex((t) => t.drill_name === drill.drill_name)
    );

    const { error: insertError } = await supabase
      .from("workout_drills")
      .insert(drillsToInsert);

    if (insertError) console.error("Drill insert warning:", insertError);

    // ✅ RETURN data instead of setting state
    return {
      success: true,
      workoutMeta: workoutRow,
      fullJson: workoutJSON,
    };

  } catch (error) {
    console.error("Generator Error:", error);
    throw error; // Throw so the component knows it failed
  }
};
